import { readFromSupabase, writeToSupabase } from './supabase'

export interface VisitEvent {
  id: string
  timestamp: string
  page: string
  referrer: string
  source: string
  sessionId: string
  language: string
  timezone: string
}

const ANALYTICS_KEY = 'sinka_analytics'
const SESSION_KEY = 'sinka_session_id'
const MAX_EVENTS = 2000

function getOrCreateSession(): string {
  let sid = sessionStorage.getItem(SESSION_KEY)
  if (!sid) {
    sid = Math.random().toString(36).substr(2, 12)
    sessionStorage.setItem(SESSION_KEY, sid)
  }
  return sid
}

function parseSource(referrer: string): string {
  if (!referrer) return 'Direct'
  try {
    const url = new URL(referrer)
    const host = url.hostname.replace('www.', '')
    if (host.includes('google')) return 'Google'
    if (host.includes('bing')) return 'Bing'
    if (
      host.includes('twitter') ||
      host.includes('t.co') ||
      host.includes('x.com')
    )
      return 'Twitter / X'
    if (host.includes('linkedin')) return 'LinkedIn'
    if (host.includes('github')) return 'GitHub'
    if (host.includes('dribbble')) return 'Dribbble'
    if (host.includes('behance')) return 'Behance'
    if (host.includes('instagram')) return 'Instagram'
    if (host.includes('facebook')) return 'Facebook'
    if (host.includes('youtube')) return 'YouTube'
    if (host.includes('reddit')) return 'Reddit'
    if (host.includes('producthunt')) return 'Product Hunt'
    return host
  } catch {
    return 'Unknown'
  }
}

export async function trackVisit(
  page: string = window.location.pathname,
): Promise<void> {
  try {
    const sessionId = getOrCreateSession()
    const existing = (await readFromSupabase<VisitEvent[]>(ANALYTICS_KEY)) || []

    // Deduplicate: same session + page within 30 seconds
    const recent = existing.find(
      (e) =>
        e.sessionId === sessionId &&
        e.page === page &&
        Date.now() - new Date(e.timestamp).getTime() < 30_000,
    )
    if (recent) return

    const event: VisitEvent = {
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date().toISOString(),
      page,
      referrer: document.referrer || '',
      source: parseSource(document.referrer),
      sessionId,
      language: navigator.language || 'unknown',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
    }

    const updated = [event, ...existing].slice(0, MAX_EVENTS)
    await writeToSupabase(ANALYTICS_KEY, updated)
  } catch {
    // Silently fail — never break the portfolio
  }
}

export async function getEvents(): Promise<VisitEvent[]> {
  try {
    const events = await readFromSupabase<VisitEvent[]>(ANALYTICS_KEY)
    return events || []
  } catch {
    return []
  }
}
